import { Injectable } from '@angular/core';

/**
 * Manages playback speed state and preferences for the bunny-player.
 * Component-level provider -- each bunny-player instance gets its own instance.
 *
 * @requirements
 * - REQ-VIDEO-101: Video playback speed controls (CB-016)
 */
@Injectable()
export class BunnyPlayerSpeedService {
  playbackSpeed: number = 1;
  availableSpeeds: number[] = [0.5, 0.75, 1, 1.25, 1.5, 2];
  showSpeedMenu: boolean = false;

  private videoElement: HTMLVideoElement | null = null;
  private iframeElement: HTMLIFrameElement | null = null;
  private isBunnyUrl: boolean = false;

  // Callback to notify the controls service when the speed menu state changes
  private onSpeedMenuToggled: (() => void) | null = null;

  registerElements(
    videoElement: HTMLVideoElement | null,
    iframeElement: HTMLIFrameElement | null,
    isBunnyUrl: boolean,
    onSpeedMenuToggled?: () => void
  ): void {
    this.videoElement = videoElement;
    this.iframeElement = iframeElement;
    this.isBunnyUrl = isBunnyUrl;
    this.onSpeedMenuToggled = onSpeedMenuToggled || null;
  }

  loadUserSpeedPreference(): void {
    const savedSpeed = localStorage.getItem('video-playback-speed');
    if (savedSpeed) {
      const speed = parseFloat(savedSpeed);
      if (this.availableSpeeds.includes(speed)) {
        this.playbackSpeed = speed;
      }
    }
  }

  saveUserSpeedPreference(): void {
    localStorage.setItem('video-playback-speed', this.playbackSpeed.toString());
  }

  setPlaybackSpeed(speed: number): void {
    this.playbackSpeed = speed;

    // Apply speed to native video element
    if (this.videoElement) {
      this.videoElement.playbackRate = speed;
    }

    // For Bunny Stream iframe, post a message (if supported)
    if (this.isBunnyUrl && this.iframeElement) {
      try {
        this.iframeElement.contentWindow?.postMessage({
          type: 'setPlaybackRate',
          value: speed
        }, '*');
      } catch (error) {
        console.log('Bunny Stream speed control not available via iframe');
      }
    }

    this.saveUserSpeedPreference();
    this.showSpeedMenu = false;
  }

  increaseSpeed(): void {
    const currentIndex = this.availableSpeeds.indexOf(this.playbackSpeed);
    if (currentIndex < this.availableSpeeds.length - 1) {
      this.setPlaybackSpeed(this.availableSpeeds[currentIndex + 1]);
    }
  }

  decreaseSpeed(): void {
    const currentIndex = this.availableSpeeds.indexOf(this.playbackSpeed);
    if (currentIndex > 0) {
      this.setPlaybackSpeed(this.availableSpeeds[currentIndex - 1]);
    }
  }

  toggleSpeedMenu(): void {
    this.showSpeedMenu = !this.showSpeedMenu;
    if (this.onSpeedMenuToggled) {
      this.onSpeedMenuToggled();
    }
  }

  formatSpeedLabel(speed: number): string {
    return speed === 1 ? 'Normal' : `${speed}x`;
  }

  destroy(): void {
    this.videoElement = null;
    this.iframeElement = null;
    this.onSpeedMenuToggled = null;
  }
}
